import {
  RelocationOrderStatus,
  RelocationOrderTransportMethod,
  RelocationOrderTransportReason,
} from "@/api-schema-enums";
import { getDateInputValidationMessage, validateDateInputString } from "@/common/helpers/date-input";
import { sortOptionsByLabel } from "@/common/helpers/sort-options-by-label";
import { isAddressFormEmpty } from "@/components/address-form-fields/_helpers/address-form-values";
import { addressFormSchema } from "@/components/address-form-fields/address-form-schema";
import { Combobox, ComboboxPaginationState } from "@/components/ui/combobox";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { RELOCATION_ORDER_STATUS_TRANSLATIONS } from "@/components/relocation-order/relocation-order-status-translations";
import { RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS } from "@/components/relocation-order/relocation-order-transport-reason-translations";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { REQUIRES_DRIVER_STATUSES } from "@/modules/fit/internal/relocation-order/admin/_constants/relocation-order-statuses";
import i18next from "i18next";
import { useEffect, useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function buildRelocationOrderAdminFormSchema() {
  return z
    .object({
      originBranch: z.string(),
      originAddress: addressFormSchema.partial().optional(),
      currentBranch: z.string().nullable().optional(),
      currentAddress: addressFormSchema.partial().optional(),
      destinationBranch: z.string(),
      destinationAddress: addressFormSchema.partial().optional(),
      earliestPickupDate: z
        .string()
        .trim()
        .min(1, i18next.t("common:PickupFromIsRequired"))
        .superRefine((value, context) => {
          const error = validateDateInputString(value);

          if (error) {
            context.addIssue({ code: "custom", message: getDateInputValidationMessage(error) });
          }
        }),
      pickedUpBy: z.string().nullable(),
      orderedBy: z.string().nullable(),
      status: z.enum(RelocationOrderStatus),
      transportCompany: z.string().nullable(),
      transportMethod: z
        .enum([RelocationOrderTransportMethod.TransportCompany, RelocationOrderTransportMethod.OwnAxle])
        .optional(),
      transportReason: z.enum(RelocationOrderTransportReason),
      vehicle: z.string().min(1, "fit:VehicleIsRequired"),
    })
    .superRefine((data, ctx) => {
      const hasOriginBranch = data.originBranch.trim().length > 0;
      const hasOriginAddress = !isAddressFormEmpty(data.originAddress);
      const hasCurrentAddress = !isAddressFormEmpty(data.currentAddress);
      const hasDestinationBranch = data.destinationBranch.trim().length > 0;
      const hasDestinationAddress = !isAddressFormEmpty(data.destinationAddress);

      if (!hasOriginBranch && !hasOriginAddress) {
        ctx.addIssue({
          code: "custom",
          message: "fit:OriginIsRequired",
          path: ["originBranch"],
        });
      }

      if (hasOriginAddress) {
        const parsedOriginAddress = addressFormSchema.safeParse(data.originAddress);

        if (!parsedOriginAddress.success) {
          for (const issue of parsedOriginAddress.error.issues) {
            ctx.addIssue({
              ...issue,
              path: ["originAddress", ...issue.path],
            });
          }
        }
      }

      if (hasCurrentAddress) {
        const parsedCurrentAddress = addressFormSchema.safeParse(data.currentAddress);

        if (!parsedCurrentAddress.success) {
          for (const issue of parsedCurrentAddress.error.issues) {
            ctx.addIssue({
              ...issue,
              path: ["currentAddress", ...issue.path],
            });
          }
        }
      }

      if (!hasDestinationBranch && !hasDestinationAddress) {
        ctx.addIssue({
          code: "custom",
          message: "fit:DestinationIsRequired",
          path: ["destinationBranch"],
        });
      }

      if (hasDestinationAddress) {
        const parsedDestinationAddress = addressFormSchema.safeParse(data.destinationAddress);

        if (!parsedDestinationAddress.success) {
          for (const issue of parsedDestinationAddress.error.issues) {
            ctx.addIssue({
              ...issue,
              path: ["destinationAddress", ...issue.path],
            });
          }
        }
      }

      if (!data.transportMethod) {
        ctx.addIssue({
          code: "custom",
          message: "fit:TransportMethodIsRequired",
          path: ["transportMethod"],
        });
      }

      if (data.transportMethod === RelocationOrderTransportMethod.TransportCompany) {
        if (!data.transportCompany || data.transportCompany.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: "fit:TransportCompanyIsRequired",
            path: ["transportCompany"],
          });
        }
      }

      if (REQUIRES_DRIVER_STATUSES.includes(data.status)) {
        if (!data.pickedUpBy) {
          ctx.addIssue({
            code: "custom",
            message: "fit:DriverRequiredForInTransit",
            path: ["pickedUpBy"],
          });
        }
      }
    });
}

export type RelocationOrderAdminFormSchemaType = z.infer<ReturnType<typeof buildRelocationOrderAdminFormSchema>>;

export type MappedTransportCompany = {
  value: string;
  label: string;
  labelShort: string;
  searchText: string;
};

export type MappedBranch = {
  value: string;
  label: string;
  searchText: string;
};

export type MappedUser = {
  value: string;
  label: string;
};

type RelocationOrderAdminFormFieldsProps = {
  form: UseFormReturn<RelocationOrderAdminFormSchemaType>;
  mappedTransportCompanies: MappedTransportCompany[];
  mappedTransportCompanyUsers: MappedUser[];
  mappedOrderedByUsers: MappedUser[];
  companyComboboxState?: ComboboxPaginationState;
  driverComboboxState?: ComboboxPaginationState;
  orderedByComboboxState?: ComboboxPaginationState;
  statuses: RelocationOrderStatus[];
};

export function RelocationOrderAdminFormFields({
  form,
  mappedTransportCompanies,
  mappedTransportCompanyUsers,
  mappedOrderedByUsers,
  companyComboboxState,
  driverComboboxState,
  orderedByComboboxState,
  statuses,
}: RelocationOrderAdminFormFieldsProps) {
  const { t } = useTranslation(["common", "fit"]);
  const selectedStatus = useWatch({ control: form.control, name: "status" });
  const selectedTransportMethod = useWatch({ control: form.control, name: "transportMethod" });
  const selectedTransportCompanyUuid = useWatch({ control: form.control, name: "transportCompany" });
  const { isSubmitted, touchedFields } = form.formState;
  const showEarliestPickupDateError = !!touchedFields.earliestPickupDate || isSubmitted;

  const transportMethodOptions = useMemo(
    () =>
      sortOptionsByLabel([
        {
          value: RelocationOrderTransportMethod.TransportCompany,
          label: t("fit:TransportMethodTransportCompany"),
          searchText: t("fit:TransportMethodTransportCompany").toLowerCase(),
        },
        {
          value: RelocationOrderTransportMethod.OwnAxle,
          label: t("fit:TransportMethodOwnAxle"),
          searchText: t("fit:TransportMethodOwnAxle").toLowerCase(),
        },
      ]),
    [t],
  );

  const transportReasonOptions = useMemo(
    () =>
      sortOptionsByLabel(
        Object.values(RelocationOrderTransportReason).map((reason) => ({
          value: reason,
          label: t(RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS[reason]),
        })),
      ),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      sortOptionsByLabel(
        statuses.map((status) => ({
          value: status,
          label: t(RELOCATION_ORDER_STATUS_TRANSLATIONS[status]),
        })),
      ),
    [statuses, t],
  );

  useEffect(() => {
    if (!REQUIRES_DRIVER_STATUSES.includes(selectedStatus)) {
      form.setValue("pickedUpBy", null);
      form.clearErrors("pickedUpBy");
    }
  }, [form, selectedStatus]);

  useEffect(() => {
    if (selectedTransportMethod === RelocationOrderTransportMethod.OwnAxle) {
      form.setValue("transportCompany", null);
      form.clearErrors("transportCompany");
    }
  }, [form, selectedTransportMethod]);

  return (
    <>
      <FormField
        control={form.control}
        name="earliestPickupDate"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel showError={showEarliestPickupDateError}>{t("common:PickupFrom")}</FormLabel>
            <FormControl showError={showEarliestPickupDateError}>
              <DatePickerInput
                ref={field.ref}
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={() => {
                  field.onBlur();
                  void form.trigger("earliestPickupDate");
                }}
              />
            </FormControl>
            <FormMessage showError={showEarliestPickupDateError} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="transportReason"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("fit:TransportReason")}</FormLabel>
            <Combobox
              options={transportReasonOptions}
              value={field.value}
              onChange={field.onChange}
              placeholder={t("fit:SelectTransportReason")}
              searchPlaceholder={t("fit:SearchTransportReason")}
              emptyText={t("fit:NoTransportReasonFound")}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="transportMethod"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("fit:TransportMethod")}</FormLabel>
            <Combobox
              options={transportMethodOptions}
              value={field.value || ""}
              onChange={field.onChange}
              placeholder={t("fit:SelectTransportMethod")}
              searchPlaceholder={t("fit:SearchTransportMethod")}
              emptyText={t("fit:NoTransportMethodFound")}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="transportCompany"
        render={({ field }) => {
          const isDisabled =
            !selectedTransportMethod || selectedTransportMethod === RelocationOrderTransportMethod.OwnAxle;
          const transportCompanyOptions = mappedTransportCompanies.map((company) => ({
            value: company.value,
            label: `${company.label} (${company.labelShort})`,
          }));

          return (
            <FormItem className="flex flex-col">
              <FormLabel>{t("fit:TransportCompany")}</FormLabel>
              <Combobox
                options={transportCompanyOptions}
                value={field.value || ""}
                onChange={(value) => {
                  field.onChange(value);
                  form.setValue("pickedUpBy", null);
                }}
                isLoading={companyComboboxState?.isLoading}
                onSearchChange={companyComboboxState?.onSearchChange}
                shouldFilter={!companyComboboxState}
                hasMore={companyComboboxState?.hasMore}
                remainingCount={companyComboboxState?.remainingCount}
                isLoadingMore={companyComboboxState?.isLoadingMore}
                onLoadMore={companyComboboxState?.onLoadMore}
                placeholder={t("fit:SelectTransportCompany")}
                searchPlaceholder={t("common:SearchCompanies")}
                emptyText={t("common:NoCompaniesFound")}
                disabled={isDisabled}
              />
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="status"
        render={({ field }) => {
          const isDisabled = !selectedTransportMethod;

          return (
            <FormItem className="flex flex-col">
              <FormLabel>{t("common:Status")}</FormLabel>
              <Combobox
                options={statusOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder={t("common:SelectStatus")}
                searchPlaceholder={t("common:SearchStatus")}
                emptyText={t("common:NoStatusFound")}
                disabled={isDisabled}
              />
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="pickedUpBy"
        render={({ field }) => {
          const isDisabledDueToStatus = !REQUIRES_DRIVER_STATUSES.includes(selectedStatus);
          const isDisabledDueToTransport =
            selectedTransportMethod === RelocationOrderTransportMethod.TransportCompany
              ? !selectedTransportCompanyUuid
              : !selectedTransportMethod;
          const isDisabled = isDisabledDueToStatus || isDisabledDueToTransport;

          return (
            <FormItem className="flex flex-col">
              <FormLabel>{t("fit:PickedUpBy")}</FormLabel>
              <Combobox
                options={mappedTransportCompanyUsers}
                value={field.value || ""}
                onChange={(value) => field.onChange(value || null)}
                isLoading={driverComboboxState?.isLoading}
                onSearchChange={driverComboboxState?.onSearchChange}
                shouldFilter={!driverComboboxState}
                hasMore={driverComboboxState?.hasMore}
                remainingCount={driverComboboxState?.remainingCount}
                isLoadingMore={driverComboboxState?.isLoadingMore}
                onLoadMore={driverComboboxState?.onLoadMore}
                placeholder={t("fit:SelectDriver")}
                searchPlaceholder={t("fit:SearchDriver")}
                emptyText={t("fit:NoDriverFound")}
                clearLabel={t("common:ClearSelection")}
                disabled={isDisabled}
              />
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="orderedBy"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("fit:RelocationOrderOrderedBy")}</FormLabel>
            <Combobox
              options={mappedOrderedByUsers}
              value={field.value || ""}
              onChange={(value) => field.onChange(value || null)}
              isLoading={orderedByComboboxState?.isLoading}
              onSearchChange={orderedByComboboxState?.onSearchChange}
              shouldFilter={!orderedByComboboxState}
              hasMore={orderedByComboboxState?.hasMore}
              remainingCount={orderedByComboboxState?.remainingCount}
              isLoadingMore={orderedByComboboxState?.isLoadingMore}
              onLoadMore={orderedByComboboxState?.onLoadMore}
              placeholder={t("fit:RelocationOrderSelectOrderedBy")}
              searchPlaceholder={t("fit:RelocationOrderSearchOrderedBy")}
              emptyText={t("fit:RelocationOrderNoOrderedByFound")}
              clearLabel={t("common:ClearSelection")}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
